// Based on @gooddata\sdk-ui\esm\execution\createExecution.js
import type { IAnalyticalBackend } from '@gooddata/sdk-backend-spi';
import { isAttribute, isMeasure, MeasureGroupIdentifier, newDimension, newTwoDimensional } from '@gooddata/sdk-model';
import type { IAttribute, IAttributeOrMeasure, INullableFilter, ISortItem, ITotal } from '@gooddata/sdk-model';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';

/**
 * When caller desires just data series and no slicing, create a single-dim result.
 */
function seriesOnlyDim(seriesBy: IAttributeOrMeasure[]) {
    return [
        newDimension(
            compact([
                ...seriesBy.filter(isAttribute),
                // only include MeasureGroupIdentifier if there are some measures, otherwise the execution will always fail on the backend
                seriesBy.some(isMeasure) && MeasureGroupIdentifier,
            ])
        ),
    ];
}

/**
 * When caller desires data series to be sliced further by some attributes (and perhaps with totals as well)
 * then create two-dim result resembling a pivot table:
 *
 * -  slices are in rows (first dim)
 * -  measures & scoping attributes will be in columns (second dim)
 */
function seriesAndSlicesDim(seriesBy: IAttributeOrMeasure[], slices: IAttribute[], totals: ITotal[]) {
    return newTwoDimensional(
        [...slices, ...totals],
        compact([
            ...seriesBy.filter(isAttribute),
            // only include MeasureGroupIdentifier if there are some measures, otherwise the execution will always fail on the backend
            seriesBy.some(isMeasure) && MeasureGroupIdentifier,
        ])
    );
}

export interface CreateExecutionOptions {
    backend?: IAnalyticalBackend;
    workspace?: string;
    seriesBy?: IAttributeOrMeasure[];
    slicesBy?: IAttribute[];
    totals?: ITotal[];
    filters?: INullableFilter[];
    sortBy?: ISortItem[];
    componentName?: string;
}

/**
 * Given execute props, this will prepare execution to send to backend.
 */
export function createExecution(options: CreateExecutionOptions) {
    const {
        backend,
        workspace,
        seriesBy = [],
        slicesBy = [],
        filters = [],
        sortBy = [],
        totals = [],
        componentName = 'Execution',
    } = options;
    if (!backend || !workspace) {
        throw new Error('backend and workspace must be either specified explicitly or be provided by context');
    }
    const dimensions = isEmpty(slicesBy) ? seriesOnlyDim(seriesBy) : seriesAndSlicesDim(seriesBy, slicesBy, totals);
    return backend
        .withTelemetry(componentName, options)
        .workspace(workspace)
        .execution()
        .forItems(seriesBy.concat(slicesBy), filters)
        .withSorting(...sortBy)
        .withDimensions(...dimensions);
}
